// components/SingleCheckoutForm.jsx
import React, { useState } from 'react';
import { Button, Form, Row, Col, Container, Card, Alert } from 'react-bootstrap';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../backendClient';
import './singlecheckoutform.scss';
import FlatStyledSingle from './FlatStyledSingle';

export default function SingleGA({
  eventId,
  slug,
  eventTitle,
  discountPrice = 0,
  ticketLabel = 'GA',
  ticketAmountCents = 0,
  isLead, 
  currency = 'usd',
  demoMode = false,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  const handleError = (err) => {
    setLoading(false);
    setErrorMessage( err?.response?.data?.message || err?.message || err?.response?.data?.error || 'Something went wrong. Please try again.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!stripe || !elements) return;
    if (!eventId) { setErrorMessage('Missing event.'); return; }

    setLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) { handleError(submitError); return; }

    const { error, paymentMethod } = await stripe.createPaymentMethod({ elements });
    if (error) { handleError(error); return; }

    try {
      const res = await axios.post('/api/payments/paymentConfirm', {
        eventId,
        paymentMethodId: paymentMethod.id,
        promoCode: promoCode || null, 
        slug
      });

      if (res.data?.status === 'requires_action' && res.data.clientSecret) {
        const { error: actErr } = await stripe.handleNextAction({ clientSecret: res.data.clientSecret });
        if (actErr) { handleError(actErr); return; }

        const fin = await axios.post('/api/payments/paymentFinalize', {
          paymentIntentId: res.data.paymentIntentId
        });

        if (fin.data?.ok && fin.data.status === 'paid') {
          navigate(`/single/${slug || ''}`, { state: { paid: true } });
        } else {
          setErrorMessage(`Payment status: ${fin.data?.status || 'unknown'}`);
        }
      } else if (res.data?.ok && res.data.status === 'paid') {
        navigate(`/single/${slug || ''}`, { state: { paid: true } });
      } else {
        setErrorMessage(res.data?.error || `Payment status: ${res.data?.status || 'unknown'}`);
      }
    } catch (e2) {
      if (demoMode) {
        setErrorMessage('Demo mode: backend route not ready. The form renders; wire /api/payments/ga/confirm.');
      } else {
        handleError(e2);
      }
    } finally {
      setLoading(false);
    }
  };

  const price = ticketAmountCents;
  const discount = 0;
  const total = Math.max(0, price - discount);

  return (
    <Container>
      <Row>
        <Col md={12} className="d-flex flex-direction-row justify-content-center align-items-center m-3">
          <h3>GA Ticket Checkout</h3>
        </Col>
        <Col md={12} className="d-flex flex-direction-row justify-content-center align-items-center m-3">
          <h6>{eventTitle}</h6>
        </Col>
        <Col md={12} className="d-flex flex-direction-row justify-content-center align-items-center m-3">
        <h6>Your ticket is immediately accessible in "MyOffers" including group chat access for any questions</h6>
        </Col>
      </Row>

      {errorMessage && (
        <Row>
          <Col>
            <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="d-flex flex-direction-row justify-content-center">
        <Col md={6} xs={12} className="m-3 order-2 order-md-1">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="promo" className="mb-3">
                  <Form.Label className="stripe-like-label">Promo Code (Optional)</Form.Label>
                  <Form.Control
                    className="stripe-like-input"
                    type="text"
                    placeholder="PROMO10"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <PaymentElement />
              </Col>
              <Col>
                <Row className="mt-2">
                  <Button variant="primary" type="submit" disabled={!stripe || loading}>
                    {loading ? 'Processing…' : price ? `Pay ${new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(price)}` : 'Pay now'}
                  </Button>
                </Row>
              </Col>
            </Row>
          </Form>
        </Col>

        <Col md={4} xs={12} className="order-1">
          <FlatStyledSingle
            membershipPrice={price || 0}
            discount={discount}
            total={total}
            title="GA Ticket (One-time)"
            totalLabel="Total (one-time)"
          />
        <Card className="flat-card">
            <Card.Body>
              <div className="membership-section">
              {isLead ?    <ul>
                   <li>Instant access to your lead deliverable link after purchase</li>
                   <li>Private chat thread for questions, replacements, and updates</li>
                   <li>Clear package details (lead type, state, fields included, total count)</li>
                   <li>Optional refreshes when new batches are available (if offered)</li>
                   <li>Support if a deliverable link has access issues</li>
                   <li>Ability to review the lead package and seller</li>
                 </ul>:
                <ul>
                  <li>Meet your advisor via in person or zoom link!</li>
                  <li>Immediate secure ticket access for check-in to venue</li>
                  <li>Group chat with Influencer Before Event</li>
                  <li>Catered food and drink or open-bar* (Subject to event description)</li>
                  <li>Full refund if host no-shows (with support)</li>
                  <li>Ability to Review Event and Host</li></ul> 
                }     
              </div>
            </Card.Body>
          </Card>
        </Col> 
      </Row>
    </Container>
  );
}
